import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
import classNames from 'classnames'
import { random } from 'lodash'

import { REACTIVE_CLAN_MESSAGE_TYPE } from 'constants'
import { CLAN_LANDING } from 'urls'

import { openExternalBrowserWithAuth } from 'helpers/web_to_client'
import { subscribeToChannel, unsubscribeFromChannel, parseHermodJSONData } from 'helpers/hermod'

import { requestBattleSchedule } from 'state/actions/wgcwx_battle_schedule'

import { Info } from 'react-wot-bootstrap'
import ButtonSimple from 'components/ButtonSimple/ButtonSimple'
import WGCWXConfirmation from 'components/WGCWXConfirmation/WGCWXConfirmation'
import BattleLog from './components/BattleLog/BattleLog'

import ERRORS, { ERROR_CODES } from './errors'

import { battleInfoPropTypes } from './propTypes'

import {
    GLOBAL_MAPS_BATTLES,
    BATTLE_SCHEDULE_INFO,
    GO_TO_GLOBAL_MAP,
    AFFIRMATIVE,
} from './translations'

import { playEnterClansBattlesGlobalMapSound, playExitClansBattlesGlobalMapSound } from 'state/actions/sounds'

import styles from './WGCWXBattleSchedule.scss'

export class WGCWXBattleSchedule extends React.Component {
    static propTypes = {
        clanChannelId: PropTypes.string.isRequired,
        globalMapUrl: PropTypes.string.isRequired,
        signInTokenUrl: PropTypes.string.isRequired,
        isInitialLoading: PropTypes.bool.isRequired,
        error: PropTypes.string,
        battleSchedule: PropTypes.arrayOf(battleInfoPropTypes),
        requestBattleSchedule: PropTypes.func.isRequired,
        close: PropTypes.func.isRequired,
        playEnterClansBattlesGlobalMapSound: PropTypes.func.isRequired,
        playExitClansBattlesGlobalMapSound: PropTypes.func.isRequired,
    }

    constructor(props) {
        super(props)

        this.state = {
            isInitialLoading: true,
            battleSchedule: [],
        }
    }

    componentDidMount() {
        this.props.playEnterClansBattlesGlobalMapSound()
        window.onBeforeUnloadHandlers.playShopSoundsOnExit = () => {
            this.props.playExitClansBattlesGlobalMapSound()
        }
        window.onBeforeUnloadHandlers.unsubscribeChannelOnExit = () => {
            this.unsubscribeChannel()
        }

        this.props.requestBattleSchedule()
        this.subscribeChannel()
    }

    componentWillUnmount() {
        this.props.playExitClansBattlesGlobalMapSound()
        delete window.onBeforeUnloadHandlers.playShopSoundsOnExit
        delete window.onBeforeUnloadHandlers.unsubscribeChannelOnExit

        this.resetBattleScheduleTimer()
        this.unsubscribeChannel()
    }

    subscribeChannel() {
        if (this.props.clanChannelId) {
            subscribeToChannel(this.props.clanChannelId, this.handleReactiveChannelMessage)
        }
    }

    unsubscribeChannel() {
        if (this.props.clanChannelId) {
            unsubscribeFromChannel(this.props.clanChannelId, this.handleReactiveChannelMessage)
        }
    }

    resetBattleScheduleTimer = () => {
        if (this.battleScheduleTimerId) {
            clearTimeout(this.battleScheduleTimerId)
        }
    }

    handleReactiveChannelMessage = (c2wEvent) => {
        const data = parseHermodJSONData(c2wEvent)
        const isBattlesUpdated = data && data.type === REACTIVE_CLAN_MESSAGE_TYPE.BATTLES_UPDATED

        if (isBattlesUpdated) {
            const timeout = random(2000, 6000)

            this.resetBattleScheduleTimer()
            this.battleScheduleTimerId = setTimeout(() => {
                this.props.requestBattleSchedule()
            }, timeout)
        }
    }

    openGlobalMap = e => {
        e.preventDefault()
        const { signInTokenUrl, globalMapUrl } = this.props
        openExternalBrowserWithAuth(signInTokenUrl, globalMapUrl)
    }

    renderError = () => {
        const { close, error } = this.props
        const errorData = ERRORS[error] ? ERRORS[error] : ERRORS[ERROR_CODES.UNKNOWN_ERROR]

        const { title, description } = errorData
        const errorClassNames = classNames(styles.base, styles.base__overlayOpened)

        return (
            <div className={errorClassNames}>
                <div className={styles.background} />
                <WGCWXConfirmation
                    messageType="error"
                    title={title}
                    description={description}
                    buttonsOptions={[
                        {
                            caption: AFFIRMATIVE,
                            callback: () => close(),
                        },
                    ]}
                    onClose={() => close()}
                />
            </div>
        )
    }

    render() {
        const {
            isInitialLoading,
            error,
            battleSchedule,
        } = this.props

        if (error) {
            return this.renderError()
        }

        return (
            <div className={styles.base}>
                <div className={styles.background} />
                <div className={styles.container}>
                    <h1 className={styles.title}>
                        {GLOBAL_MAPS_BATTLES}
                    </h1>
                    <div className={styles.infoContainer}>
                        <div className={styles.infoIcon}>
                            <Info />
                        </div>
                        <div className={styles.info}>
                            {BATTLE_SCHEDULE_INFO}
                        </div>
                        <div className={styles.mapButton}>
                            <ButtonSimple
                                onClick={this.openGlobalMap}
                                isExternal
                            >
                                {GO_TO_GLOBAL_MAP}
                            </ButtonSimple>
                        </div>
                    </div>
                    <BattleLog
                        isInitialLoading={isInitialLoading}
                        battleSchedule={battleSchedule}
                    />
                </div>
            </div>
        )
    }
}

export const stateToProps = ({ settings, wgcwxBattleSchedule, wgcwxClanInfo }) => {
    const clanId = wgcwxClanInfo.clan_info && wgcwxClanInfo.clan_info.id
    return {
        clanChannelId: clanId && `gm_clan_${clanId}`,
        globalMapUrl: settings.globalMapUrl,
        signInTokenUrl: settings.signInTokenUrl,
        isInitialLoading: wgcwxBattleSchedule.isInitialLoading,
        error: wgcwxBattleSchedule.errorMessage,
        battleSchedule: wgcwxBattleSchedule.battleSchedule,
    }
}

const dispatchToProps = (dispatch) => ({
    requestBattleSchedule: () => dispatch(requestBattleSchedule()),
    close: () => dispatch(push(CLAN_LANDING)),
    playEnterClansBattlesGlobalMapSound: () => dispatch(playEnterClansBattlesGlobalMapSound()),
    playExitClansBattlesGlobalMapSound: () => dispatch(playExitClansBattlesGlobalMapSound()),
})

export default connect(stateToProps, dispatchToProps)(WGCWXBattleSchedule)



// WEBPACK FOOTER //
// ./containers/WGCWXBattleSchedule/WGCWXBattleSchedule.jsx